import {
  addHours,
  addMinutes,
  isAfter,
  isBefore,
  startOfDay,
  startOfHour,
  startOfSecond,
} from 'date-fns'
import isNil from 'lodash-es/isNil'

import { EMPTY_LIST, EMPTY_STRING } from '../../../constants/common'
import { createNowDateInUkTimezone } from '../../../utils/date'
import { toFlatSelectItem } from '../../Flat'
import { toRequestSelectItem } from '../../Request'
import {
  APPOINTMENT_REMINDER_DEFAULT_MINUTES,
  AppointmentType,
} from '../Appointment.constants'

import {
  APPOINTMENT_FORM_DEFAULT_DURATION_MINUTES,
  APPOINTMENT_FORM_FIELD_NAME,
  APPOINTMENT_FORM_SPAN_ERROR_KEY,
} from './AppointmentFormModal.constants'
import { getAppointmentFormMockParticipants } from './AppointmentFormModal.utils.participants'

function getAppointmentFormNextFullHour(timezone) {
  const now = startOfSecond(createNowDateInUkTimezone(timezone))

  const hour = startOfHour(now)

  return isBefore(hour, now) ? addHours(hour, 1) : hour
}

function toAppointmentFormMockAnchor(item) {
  return item ? { id: item.value, name: item.label } : null
}

export function getAppointmentFormEnd(start) {
  return start
    ? addMinutes(start, APPOINTMENT_FORM_DEFAULT_DURATION_MINUTES)
    : null
}

export function getAppointmentFormInitialValues({
  end,
  location = EMPTY_STRING,
  request,
  start,
  timezone,
  type = AppointmentType.general,
}) {
  const startValue = start ?? getAppointmentFormNextFullHour(timezone)

  return {
    [APPOINTMENT_FORM_FIELD_NAME.building]: toAppointmentFormBuildingItem(
      request?.building
    ),
    [APPOINTMENT_FORM_FIELD_NAME.description]: EMPTY_STRING,
    [APPOINTMENT_FORM_FIELD_NAME.end]: end ?? getAppointmentFormEnd(startValue),
    [APPOINTMENT_FORM_FIELD_NAME.firstDay]: startOfDay(startValue),
    [APPOINTMENT_FORM_FIELD_NAME.flat]: toAppointmentFormFlatItem(
      request?.flat
    ),
    [APPOINTMENT_FORM_FIELD_NAME.isAllDay]: false,
    [APPOINTMENT_FORM_FIELD_NAME.lastDay]: startOfDay(startValue),
    [APPOINTMENT_FORM_FIELD_NAME.location]: location,
    [APPOINTMENT_FORM_FIELD_NAME.participants]: EMPTY_LIST,
    [APPOINTMENT_FORM_FIELD_NAME.reminder]:
      APPOINTMENT_REMINDER_DEFAULT_MINUTES,
    [APPOINTMENT_FORM_FIELD_NAME.request]: request
      ? toRequestSelectItem(request)
      : null,
    [APPOINTMENT_FORM_FIELD_NAME.start]: startValue,
    [APPOINTMENT_FORM_FIELD_NAME.title]: EMPTY_STRING,
    [APPOINTMENT_FORM_FIELD_NAME.type]: type,
  }
}

export function getAppointmentFormTranslatedOptions({ options, t }) {
  return options.map(option => ({ ...option, label: t(option.label) }))
}

export function getIsAppointmentFormSpanInvalid(values) {
  if (values[APPOINTMENT_FORM_FIELD_NAME.isAllDay]) {
    const firstDay = values[APPOINTMENT_FORM_FIELD_NAME.firstDay]
    const lastDay = values[APPOINTMENT_FORM_FIELD_NAME.lastDay]

    return !isNil(firstDay) && !isNil(lastDay) && isBefore(lastDay, firstDay)
  }

  const start = values[APPOINTMENT_FORM_FIELD_NAME.start]
  const end = values[APPOINTMENT_FORM_FIELD_NAME.end]

  return !isNil(start) && !isNil(end) && !isAfter(end, start)
}

export function getAppointmentFormSpanErrorKey(values) {
  return getIsAppointmentFormSpanInvalid(values)
    ? APPOINTMENT_FORM_SPAN_ERROR_KEY
    : null
}

export function getAppointmentFormDateErrorProps(spanErrorKey) {
  return error => ({ showError: Boolean(error || spanErrorKey) })
}

export function getIsAppointmentFormSubmittable(values) {
  const [spanStart, spanEnd] = values[APPOINTMENT_FORM_FIELD_NAME.isAllDay]
    ? [
        values[APPOINTMENT_FORM_FIELD_NAME.firstDay],
        values[APPOINTMENT_FORM_FIELD_NAME.lastDay],
      ]
    : [
        values[APPOINTMENT_FORM_FIELD_NAME.start],
        values[APPOINTMENT_FORM_FIELD_NAME.end],
      ]

  return (
    values[APPOINTMENT_FORM_FIELD_NAME.title].trim().length > 0 &&
    !isNil(spanStart) &&
    !isNil(spanEnd) &&
    !getIsAppointmentFormSpanInvalid(values)
  )
}

export function getAppointmentFormAnchorHintKey(values) {
  if (values[APPOINTMENT_FORM_FIELD_NAME.request]) {
    return 'Appointment.FormAnchorFromRequestKey'
  }

  return values[APPOINTMENT_FORM_FIELD_NAME.building] ||
    values[APPOINTMENT_FORM_FIELD_NAME.flat]
    ? null
    : 'Appointment.FormNoAnchorHintKey'
}

export function toAppointmentFormBuildingItem(building) {
  return building
    ? { label: building.title || building.address?.value, value: building.id }
    : null
}

export function toAppointmentFormFlatItem(flat) {
  return flat ? toFlatSelectItem(flat) : null
}

export function getAppointmentFormMockContext({ timezone, userData, values }) {
  const building = toAppointmentFormMockAnchor(
    values[APPOINTMENT_FORM_FIELD_NAME.building]
  )
  const flat = toAppointmentFormMockAnchor(
    values[APPOINTMENT_FORM_FIELD_NAME.flat]
  )
  const request = values[APPOINTMENT_FORM_FIELD_NAME.request]

  return {
    anchors: {
      building,
      flat,
      request: request && { building, flat, title: request.label },
    },
    participants: getAppointmentFormMockParticipants({
      participants: values[APPOINTMENT_FORM_FIELD_NAME.participants],
      userData,
    }),
    timezone,
    user: userData && { id: userData.id, name: userData.fullname },
  }
}
